import { Lightning, Router, Utils } from "@lightningjs/sdk";
import { List } from "@lightningjs/ui";
import { GameCard } from "./GameCard";
import { GlassScene } from "./GlassScene";
import { ScoreBanner } from "./ScoreBanner";
import { HeroMotion } from "./HeroMotion";
import { FOCUS_TRANSITION, UNFOCUSED_ROW_ALPHA } from "./focus";
export class Hero extends Lightning.Component {
    constructor() {
        super(...arguments);
        this._items = [];
        this._hasAppeared = false;
        this.Games = this.tag("Content").tag("Games");
        this._motion = new HeroMotion(this.tag("Content").tag("Backdrop").tag("Players"), this.tag("Content").tag("ScoreBanner"), this.tag("Content").tag("Preload"), (item) => this._updateBackdrop(item));
    }
    static _template() {
        return {
            w: this.W,
            h: this.H,
            alpha: 1,
            Content: {
                type: GlassScene,
                signals: { onGameSelected: true },
                // Home's rows are inset by 115px; the hero artwork fills the screen.
                x: -115,
                w: this.W,
                h: this.H,
                clipping: true,
                // Fade the composed scene so overlapping player cutouts stay solid.
                renderToTexture: true,
                Backdrop: {
                    forceZIndexContext: true,
                    w: this.W,
                    h: this.H,
                    rect: true,
                    color: 0xff161616,
                    Background: {
                        x: 326,
                        w: 1593,
                        h: this.H,
                        clipping: true,
                        Pattern: {
                            w: 1939,
                            h: 1090,
                            alpha: 0.5,
                            src: Utils.asset("images/hero/ligue-1-pattern.png"),
                        },
                    },
                    Players: { x: -1, w: this.W, h: this.H, zIndex: 1 },
                    LeftFade: {
                        x: 423.65,
                        y: 1,
                        w: 553.35,
                        h: 1079,
                        rect: true,
                        colorLeft: 0xff161616,
                        colorRight: 0x00161616,
                    },
                    LeftEdge: {
                        x: 326,
                        y: 1,
                        w: 97.65,
                        h: 1079,
                        rect: true,
                        color: 0xff161616,
                    },
                    Fade: {
                        zIndex: 2,
                        x: 326,
                        w: 1593,
                        rect: true,
                        colorTop: 0x00161616,
                        colorBottom: 0xff161616,
                    },
                    Bottom: {
                        x: 326,
                        w: 1593,
                        rect: true,
                        color: 0xff161616,
                        zIndex: 2,
                    },
                },
                ScoreBanner: { x: 115, y: 307.883, type: ScoreBanner, visible: false },
                Title: {
                    x: 115,
                    y: 757.153,
                    text: {
                        text: "Live & Upcoming Games",
                        fontFace: "Roboto",
                        fontStyle: "700",
                        fontSize: 32,
                        precision: 1,
                        h: 34,
                        lineHeight: 34,
                        offsetY: 28,
                        letterSpacing: -0.64,
                        textColor: 0xffffffff,
                    },
                },
                Sponsors: {
                    x: 1656.692,
                    y: 757.653,
                    w: 148.308,
                    h: 33,
                    McDonalds: {
                        w: 38,
                        h: 33,
                        texture: Lightning.Tools.getSvgTexture(Utils.asset("images/hero/sponsor-mcdonalds.svg"), 76, 66),
                    },
                    Bkt: {
                        x: 51.308,
                        w: 97,
                        h: 33,
                        texture: Lightning.Tools.getSvgTexture(Utils.asset("images/hero/sponsor-bkt.svg"), 194, 66),
                    },
                },
                Games: {
                    x: 115,
                    y: 811.153,
                    type: List,
                    scroll: { after: 5 },
                    scrollTransition: {
                        duration: 0.3,
                        timingFunction: "ease-out",
                    },
                    passSignals: { onIndexChanged: "onGameSelected" },
                },
                Arrow: {
                    x: 950,
                    y: 1038.171,
                    w: 20.226,
                    h: 9.015,
                    src: Utils.asset("images/hero/arrow.png"),
                },
                Preload: { w: 1, h: 1 },
            },
        };
    }
    set items(items) {
        this._items = items;
        this._updateBackdrop(items[0]);
        this.Games.patch({
            items: items.map((item) => ({ type: GameCard, item })),
        });
        this.Games.setIndex(0);
        this.onGameSelected({ index: 0 });
    }
    onGameSelected({ index }) {
        // Defer the first entrance until the hero is visible, regardless of focus.
        if (!this.active && !this._hasAppeared)
            return;
        this._motion.show(this._items[index], this.active);
    }
    _updateBackdrop(item) {
        var _a;
        const fadeHeight = (_a = item === null || item === void 0 ? void 0 : item.fadeHeight) !== null && _a !== void 0 ? _a : 511;
        this.patch({
            Content: {
                Backdrop: {
                    Players: { zIndex: (item === null || item === void 0 ? void 0 : item.fadePlayers) ? 0 : 1 },
                    Fade: { y: Hero.H - fadeHeight, h: fadeHeight * 0.85 },
                    Bottom: { y: Hero.H - fadeHeight * 0.15, h: fadeHeight * 0.15 },
                },
            },
        });
    }
    _active() {
        var _a;
        // A cached Home can return while Navbar keeps focus, without _focus firing.
        if (((_a = Router.getActiveWidget()) === null || _a === void 0 ? void 0 : _a.ref) === "Navbar") {
            this.setSmooth("alpha", 1, FOCUS_TRANSITION);
        }
        this._hasAppeared = true;
        this.onGameSelected({ index: this.Games.index });
    }
    _inactive() {
        // A hidden hero should return fully settled, with no delayed callbacks.
        this._motion.show(this._items[this.Games.index], false);
    }
    _detach() {
        this._motion.dispose();
    }
    _getFocused() {
        return this.Games;
    }
    _focus() {
        this.setSmooth("alpha", 1, FOCUS_TRANSITION);
    }
    _unfocus() {
        var _a;
        // The hero remains the backdrop while navigating the Navbar above it.
        const navbarFocused = ((_a = Router.getActiveWidget()) === null || _a === void 0 ? void 0 : _a.ref) === "Navbar";
        this.setSmooth("alpha", navbarFocused ? 1 : UNFOCUSED_ROW_ALPHA, FOCUS_TRANSITION);
    }
}
Hero.W = 1920;
Hero.H = 1080;
export default Hero;
//# sourceMappingURL=Hero.js.map